import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Banknote, ArrowUpCircle, ArrowDownCircle, Wallet, Calculator, Search, Plus, Loader2,
  Settings2, Pencil, Trash2, User,
} from "lucide-react";
import {
  listCashMovements, createCashMovement,
  listCashCategories, upsertCashCategory, deleteCashCategory,
} from "@/lib/api/finance.functions";
import { paymentMethodLabel } from "@/lib/payment-methods";
import { brToday } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/m/caixa")({
  validateSearch: (s: Record<string, unknown>) => ({
    prefill: typeof s.prefill === "string" ? s.prefill : undefined,
    desc: typeof s.desc === "string" ? s.desc : undefined,
    amount: typeof s.amount === "string" ? s.amount : undefined,
    method: typeof s.method === "string" ? s.method : undefined,
  }),
  component: CaixaPage,
});

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

const firstOfMonth = () => {
  return `${brToday().slice(0, 7)}-01`;
};
const today = () => brToday();

const ALL_CATEGORIES = "__all__";
const NO_CATEGORY = "__none__";

function CaixaPage() {
  const listFn = useServerFn(listCashMovements);
  const createFn = useServerFn(createCashMovement);
  const listCatsFn = useServerFn(listCashCategories);
  const search = Route.useSearch();

  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [mgrOpen, setMgrOpen] = useState(false);
  const [prefill, setPrefill] = useState<any>(null);
  const [catFilter, setCatFilter] = useState<string>(ALL_CATEGORIES);
  const [breakdownKind, setBreakdownKind] = useState<"entrada" | "saida" | null>(null);

  useEffect(() => {
    if (search.prefill === "1") {
      setPrefill({
        kind: "entrada",
        description: search.desc || "",
        amount: search.amount || "0",
        method: (search.method || "dinheiro").replace("_presencial", "").replace("cartao", "cartao_credito"),
      });
      setAddOpen(true);
    }
  }, [search.prefill, search.desc, search.amount, search.method]);

  const loadCats = () => listCatsFn().then((r: any) => setCategories(r.categories ?? [])).catch(() => {});

  const load = async () => {
    setLoading(true);
    try {
      const r = await listFn({ data: {
        start: new Date(start + "T00:00:00").toISOString(),
        end: new Date(end + "T23:59:59").toISOString(),
      }});
      setItems(r.movements ?? []);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); loadCats(); /* eslint-disable-next-line */ }, []);

  const filteredItems = useMemo(() => {
    if (catFilter === ALL_CATEGORIES) return items;
    if (catFilter === NO_CATEGORY) return items.filter((m) => !m.category_id && !m.category);
    return items.filter((m) => m.category_id === catFilter);
  }, [items, catFilter]);

  const totals = useMemo(() => {
    let entradas = 0, saidas = 0, dinheiro = 0;
    for (const m of filteredItems) {
      const v = Number(m.amount) || 0;
      if (m.kind === "entrada") entradas += v;
      else saidas += v;
      if (m.payment_method === "dinheiro") dinheiro += m.kind === "entrada" ? v : -v;
    }
    return { entradas, saidas, dinheiro, saldo: entradas - saidas };
  }, [filteredItems]);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-primary/15 p-3 text-primary"><Banknote className="h-7 w-7" /></div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Caixa</h1>
            <p className="text-sm text-muted-foreground mt-1">Reconciliação financeira e listagem de movimentações.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
          <span className="text-muted-foreground text-sm">a</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>Todas as categorias</SelectItem>
              <SelectItem value={NO_CATEGORY}>Sem categoria</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Buscar
          </Button>
          <Button variant="outline" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Lançamento
          </Button>
          <Button variant="outline" onClick={() => setMgrOpen(true)} title="Gerenciar categorias">
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={ArrowUpCircle} label="Entradas" value={fmtMoney(totals.entradas)} className="text-emerald-600"
          onDetail={() => setBreakdownKind("entrada")} />
        <StatCard icon={ArrowDownCircle} label="Saídas" value={fmtMoney(totals.saidas)} className="text-rose-600"
          onDetail={() => setBreakdownKind("saida")} />
        <div>
          <StatCard icon={Wallet} label="Total em Dinheiro" value={fmtMoney(totals.dinheiro)}
            className={totals.dinheiro < 0 ? "text-rose-600" : "text-primary"} />
          {totals.dinheiro < 0 && (
            <div className="mt-1 text-[11px] font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1">
              ⚠️ Caixa em dinheiro está negativo
            </div>
          )}
        </div>
        <StatCard icon={Calculator} label="Saldo do Período"
          value={fmtMoney(totals.saldo)} className={totals.saldo >= 0 ? "text-emerald-600" : "text-rose-600"} />
      </div>

      <BreakdownModal
        kind={breakdownKind}
        items={filteredItems}
        onClose={() => setBreakdownKind(null)}
      />


      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b">
            <h2 className="font-bold text-lg">Movimentações</h2>
          </div>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : filteredItems.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              Nenhuma movimentação encontrada para este período
            </div>
          ) : (
            <div className="divide-y">
              {filteredItems.map((m) => (
                <div key={m.id} className="p-4 flex items-center gap-3 hover:bg-muted/30">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    m.kind === "entrada" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                  }`}>
                    {m.kind === "entrada" ? <ArrowUpCircle className="h-5 w-5" /> : <ArrowDownCircle className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span className="truncate">{m.description}</span>
                      {m.bank_reconciled === false && (
                        <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700 bg-amber-50 shrink-0">
                          Aguardando extrato
                        </Badge>
                      )}
                      {m.bank_reconciled === true && (
                        <Badge variant="outline" className="text-[10px] border-emerald-500 text-emerald-700 bg-emerald-50 shrink-0">
                          Conferido no extrato
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground flex gap-2 mt-0.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{paymentMethodLabel(m.payment_method)}</Badge>
                      {(m.category_name || m.category) && (
                        <Badge variant="secondary" className="text-[10px]">{m.category_name || m.category}</Badge>
                      )}
                      <span>• {new Date(m.occurred_at).toLocaleString("pt-BR")}</span>
                      {m.created_by_name && (
                        <span className="inline-flex items-center gap-1">
                          • <User className="h-3 w-3" /> {m.created_by_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`font-bold ${m.kind === "entrada" ? "text-emerald-600" : "text-rose-600"}`}>
                    {m.kind === "entrada" ? "+" : "−"} {fmtMoney(Number(m.amount))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddMovementModal
        open={addOpen}
        categories={categories}
        onOpenChange={(o: boolean) => { setAddOpen(o); if (!o) setPrefill(null); }}
        prefill={prefill}
        onCategoriesChanged={loadCats}
        currentCash={totals.dinheiro}
        onCreate={async (p: any) => {
          if (p.kind === "saida" && p.payment_method === "dinheiro") {
            const after = totals.dinheiro - (Number(p.amount) || 0);
            if (after < 0) {
              const ok = window.confirm(
                "Não há dinheiro suficiente em caixa para este pagamento.\n\n" +
                "É recomendável lançar primeiro uma ENTRADA no caixa e depois registrar a saída.\n\n" +
                `Se prosseguir, o caixa em dinheiro ficará negativo (${fmtMoney(after)}).\n\nDeseja continuar mesmo assim?`
              );
              if (!ok) return;
            }
          }
          try { await createFn({ data: p }); toast.success("Lançado"); setAddOpen(false); setPrefill(null); load(); }
          catch (e: any) { toast.error(e?.message ?? "Erro"); }
        }}
      />


      <CategoryManagerModal
        open={mgrOpen}
        onOpenChange={setMgrOpen}
        categories={categories}
        onChanged={loadCats}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, className, onDetail }: any) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">{label}</div>
          <Icon className={`h-4 w-4 ${className}`} />
        </div>
        <div className={`text-2xl font-bold mt-1 ${className}`}>{value}</div>
        {onDetail && (
          <button
            type="button"
            onClick={onDetail}
            className="mt-2 text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Detalhar por grupo
          </button>
        )}
      </CardContent>
    </Card>
  );
}

function BreakdownModal({ kind, items, onClose }: { kind: "entrada" | "saida" | null; items: any[]; onClose: () => void }) {
  const rows = useMemo(() => {
    if (!kind) return [] as { name: string; total: number; count: number }[];
    const map = new Map<string, { name: string; total: number; count: number }>();
    for (const m of items) {
      if (m.kind !== kind) continue;
      const name = m.category_name || m.category || "Sem categoria";
      const cur = map.get(name) ?? { name, total: 0, count: 0 };
      cur.total += Number(m.amount) || 0;
      cur.count += 1;
      map.set(name, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [kind, items]);

  const grandTotal = rows.reduce((s, r) => s + r.total, 0);

  return (
    <Dialog open={!!kind} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{kind === "entrada" ? "Entradas por grupo" : "Saídas por grupo"}</DialogTitle>
          <DialogDescription>Total do período agrupado por categoria.</DialogDescription>
        </DialogHeader>
        {rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Sem movimentações no período.</div>
        ) : (
          <div className="divide-y max-h-[60vh] overflow-auto">
            {rows.map((r) => {
              const pct = grandTotal > 0 ? (r.total / grandTotal) * 100 : 0;
              return (
                <div key={r.name} className="py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium truncate">{r.name}</div>
                    <div className={`font-bold ${kind === "entrada" ? "text-emerald-600" : "text-rose-600"}`}>
                      {fmtMoney(r.total)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-0.5">
                    <span>{r.count} lançamento{r.count > 1 ? "s" : ""}</span>
                    <span>{pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 rounded bg-muted mt-1 overflow-hidden">
                    <div
                      className={kind === "entrada" ? "h-full bg-emerald-500" : "h-full bg-rose-500"}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="pt-3 flex items-center justify-between font-semibold">
              <span>Total</span>
              <span>{fmtMoney(grandTotal)}</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMovementModal({ open, onOpenChange, onCreate, prefill, categories, onCategoriesChanged }: any) {
  const upsertCatFn = useServerFn(upsertCashCategory);
  const [kind, setKind] = useState<"entrada" | "saida">("saida");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("0");
  const [method, setMethod] = useState("dinheiro");
  const [categoryId, setCategoryId] = useState<string>(NO_CATEGORY);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  useEffect(() => {
    if (open) {
      if (prefill) {
        setKind(prefill.kind || "entrada");
        setDescription(prefill.description || "");
        setAmount(String(prefill.amount || "0"));
        setMethod(prefill.method || "dinheiro");
        setCategoryId(NO_CATEGORY);
      } else {
        setKind("saida"); setDescription(""); setAmount("0"); setMethod("dinheiro"); setCategoryId(NO_CATEGORY);
      }
    }
  }, [open, prefill]);

  const filteredCats = (categories || []).filter((c: any) => c.kind === "both" || c.kind === kind);

  const createInline = async () => {
    if (!newCatName.trim()) return;
    try {
      const r: any = await upsertCatFn({ data: { name: newCatName.trim(), kind: "both" } });
      toast.success("Categoria criada");
      setNewCatName(""); setNewCatOpen(false);
      onCategoriesChanged?.();
      setCategoryId(r.id);
    } catch (e: any) { toast.error(e?.message || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo Lançamento</DialogTitle>
          <DialogDescription>Registre uma entrada ou saída manual no caixa.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Tipo</Label>
            <Select value={kind} onValueChange={(v: any) => setKind(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="entrada">Entrada</SelectItem>
                <SelectItem value="saida">Saída</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Categoria (opcional)</Label>
            <div className="flex gap-2">
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Sem categoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CATEGORY}>Sem categoria</SelectItem>
                  {filteredCats.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="icon" onClick={() => setNewCatOpen((v) => !v)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {newCatOpen && (
              <div className="flex gap-2 mt-2">
                <Input placeholder="Nome da categoria" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} />
                <Button type="button" onClick={createInline}>Criar</Button>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label>Descrição *</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <Label>Valor *</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label>Forma</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dinheiro">Dinheiro</SelectItem>
                <SelectItem value="pix">Pix</SelectItem>
                <SelectItem value="cartao_credito">Cartão</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => {
            if (!description.trim()) { toast.error("Descrição obrigatória"); return; }
            const selectedCat = filteredCats.find((c: any) => c.id === categoryId);
            onCreate({
              kind,
              description: description.trim(),
              amount: Number(amount) || 0,
              payment_method: method,
              category: selectedCat?.name ?? null,
              category_id: categoryId === NO_CATEGORY ? null : categoryId,
            });
          }}>Lançar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryManagerModal({ open, onOpenChange, categories, onChanged }: any) {
  const upsertFn = useServerFn(upsertCashCategory);
  const delFn = useServerFn(deleteCashCategory);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"entrada" | "saida" | "both">("both");
  const [editingId, setEditingId] = useState<string | null>(null);

  const reset = () => { setName(""); setKind("both"); setEditingId(null); };

  const save = async () => {
    if (!name.trim()) return;
    try {
      await upsertFn({ data: { id: editingId ?? null, name: name.trim(), kind } });
      toast.success(editingId ? "Categoria atualizada" : "Categoria criada");
      reset(); onChanged?.();
    } catch (e: any) { toast.error(e?.message || "Erro"); }
  };
  const remove = async (id: string) => {
    if (!confirm("Excluir categoria?")) return;
    try { await delFn({ data: { id } }); toast.success("Excluída"); onChanged?.(); }
    catch (e: any) { toast.error(e?.message || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Categorias do Caixa</DialogTitle>
          <DialogDescription>Gerencie os grupos de entrada e saída.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_130px_auto] gap-2">
            <Input placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} />
            <Select value={kind} onValueChange={(v: any) => setKind(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Ambos</SelectItem>
                <SelectItem value="entrada">Entrada</SelectItem>
                <SelectItem value="saida">Saída</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={save}>{editingId ? "Salvar" : "Adicionar"}</Button>
          </div>
          {editingId && (
            <Button variant="ghost" size="sm" onClick={reset}>Cancelar edição</Button>
          )}
          <div className="divide-y border rounded">
            {(categories || []).length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Nenhuma categoria.</div>
            ) : (
              categories.map((c: any) => (
                <div key={c.id} className="p-3 flex items-center gap-2">
                  <div className="flex-1">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.kind === "both" ? "Entrada e Saída" : c.kind}</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => { setEditingId(c.id); setName(c.name); setKind(c.kind); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(c.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
